import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { SettingsApiClient } from '../api/settingsApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    settings: coreTypes.Settings | null;
    settingsLoading: boolean;
    settingsLoadError: any | null;
    settingsSaveLoading: boolean;
    settingsSaveSucess: boolean;
    settingsSaveError: any | null;
}

export const settingsDefaultState: StateShape = {
    settings: null,
    settingsLoading: false,
    settingsLoadError: null,
    settingsSaveLoading: false,
    settingsSaveSucess: false,
    settingsSaveError: null,
};

const apiClient = new SettingsApiClient();

export const actionCreators = {
    loadSettings: (): ThunkResult<Promise<coreTypes.Settings | undefined>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(settingsReducerActions.SETTINGS_LOAD_START());

            try {
                const settings = await apiClient.getSettings({
                    accessToken,
                });

                dispatch(settingsReducerActions.SETTINGS_LOAD_END({ settings }));

                return settings;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    settingsReducerActions.SETTINGS_LOAD_END({ error: { message: err.message } })
                );
            }
        };
    },
    saveSettings: (settings: coreTypes.Settings | {}): ThunkResult<Promise<boolean | void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;

            dispatch(settingsReducerActions.SETTINGS_SAVE_START());

            try {
                const updatedSettings = await apiClient.saveSettings(settings, {
                    accessToken,
                });

                dispatch(settingsReducerActions.SETTINGS_SAVE_END({ settings: updatedSettings }));
                return true;
            } catch (err: any) {
                console.error(err);
                dispatch(
                    settingsReducerActions.SETTINGS_SAVE_END({
                        error: { message: err.message },
                    })
                );
                return false;
            }
        };
    },
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState: settingsDefaultState,
    reducers: {
        SETTINGS_LOAD_START(draft) {
            draft.settingsLoading = true;
            draft.settings = null;
            draft.settingsLoadError = null;
        },
        SETTINGS_LOAD_END(
            draft,
            action: PayloadAction<{ settings?: coreTypes.Settings; error?: any }>
        ) {
            const { settings, error } = action.payload;
            draft.settingsLoading = false;
            draft.settings = settings || null;
            draft.settingsLoadError = error;
        },
        SETTINGS_SAVE_START(draft) {
            draft.settingsSaveLoading = true;
            draft.settingsSaveError = null;
        },
        SETTINGS_SAVE_END(
            draft,
            action: PayloadAction<{ settings?: coreTypes.Settings; error?: any }>
        ) {
            const { settings, error } = action.payload;
            draft.settingsSaveLoading = false;
            draft.settingsSaveSucess = !error && true;
            draft.settings = settings || null;
            draft.settingsSaveError = error;
        },
    },
});

export const settingsReducerActions: any = settingsSlice.actions;
export const settingsReducer = settingsSlice.reducer;
